import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, ExternalLink, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import useQuickPanelData from "../../hooks/useQuickPanelData";
import { CustomerMark } from "../customers/CustomerBits";
import { PilotTag } from "../PilotBits";
import ProspectActionsMenu from "./ProspectActionsMenu";
import QuickActionButton from "./QuickActionButton";
import { STAGE_LABEL, INTERACTION_LABELS, ACQUISITION_CHANNEL_LABEL, parseAcquisitionChannels, prospectSourceLabel, formatRelativeDate } from "../../lib/pipeline";
import { dueLabel, quickActions, locationLabel, inactiveLabel } from "../../lib/pipelineCard";
import { openTasks, summariseExchanges } from "../../lib/quickPanel";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { MobileSheet, MobileSheetContent, MobileSheetHeader, MobileSheetTitle, MobileSheetBody, MobileSheetFooter } from "../ui/mobile-sheet";

const TONES = { danger: "text-red-700", warning: "text-orange-700", muted: "text-slate-600" };
const TARGET = "h-8 w-8 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11";
const BIG = "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-slate-200 bg-white px-3 h-9 text-sm font-medium text-slate-800 hover:bg-slate-50 hover:text-jamora focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:h-11";

function Section({ title, children, testid }) {
  return (
    <section className="py-3 border-b border-slate-100 last:border-b-0" data-testid={testid}>
      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
      {children}
    </section>
  );
}

function Contact({ icon: Icon, children, href, label }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-800">
      <Icon size={14} className="shrink-0 text-slate-500" aria-hidden />
      {href ? <a href={href} className="truncate text-jamora hover:underline" aria-label={label}>{children}</a> : <span className="truncate">{children}</span>}
    </div>
  );
}

// The quick panel of a Pipeline card (Pipeline proposal D-04): the essentials of one pharmacy and its quick actions, from the three
// existing reads. It never repeats the full record (tabs, questionnaire, migration…), which stays one real link away.
export default function QuickPanel({
  id, mode, seed, today, can, canAssign, canMove, withTasks, customerBadge, nameOf, version, onClose, onQuick, onMove,
}) {
  const navigate = useNavigate();
  const data = useQuickPanelData(id, { withTasks, version });
  const p = useMemo(() => ({ ...(seed || {}), ...(data.prospect || {}), id }), [seed, data.prospect, id]);
  const actions = quickActions(p, { can, hasCustomer: !!customerBadge });
  const tasks = useMemo(() => openTasks(Array.isArray(data.tasks) ? data.tasks : []), [data.tasks]);
  const exchanges = useMemo(() => summariseExchanges(Array.isArray(data.feed) ? data.feed : []), [data.feed]);
  const next = tasks[0] ? { title: tasks[0].title, due_date: tasks[0].due_date } : null;
  const due = next ? dueLabel(next, today) : null;
  const inactive = seed ? inactiveLabel(seed) : null;
  const ready = data.status === "ready";
  const assigneeName = nameOf ? nameOf(p.assigned_to) : null;
  const channels = parseAcquisitionChannels(p.acquisition_channel);

  const title = p.pharmacy_name || (data.status === "loading" ? "Chargement…" : "Fiche rapide");
  const heading = (Tag) => (
    <Tag tabIndex={-1} data-testid="panel-title" className="min-w-0 flex-1 text-base font-semibold leading-snug text-slate-900 outline-none">
      {title}
    </Tag>
  );

  const header = (
    <div className={`px-4 pt-4 pb-3 border-b border-slate-200 ${mode === "drawer" ? "pr-12" : ""}`} data-testid="panel-header">
      <div className="flex items-start gap-2">
        {mode === "drawer" ? <SheetTitle asChild>{heading("h2")}</SheetTitle> : <MobileSheetTitle asChild>{heading("h2")}</MobileSheetTitle>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {p.status ? <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-1 rounded stage-${p.status}`} data-testid="panel-stage">{STAGE_LABEL[p.status] || p.status}</span> : null}
        <CustomerMark badge={customerBadge} />
        {p.pilot_status ? <PilotTag status={p.pilot_status} testid="panel-pilot" /> : null}
        {inactive ? <span className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-2xs font-medium text-amber-800">{inactive}</span> : null}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-600">
        <span data-testid="panel-owner">{data.status === "unavailable" ? "" : p.assigned_to ? `Responsable : ${assigneeName || "Utilisateur"}` : "Non assigné"}</span>
        <Link to={`/prospects/${id}`} className="inline-flex items-center gap-1 font-medium text-jamora hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora" data-testid="panel-open-full">
          Ouvrir la fiche complète <ExternalLink size={12} aria-hidden />
        </Link>
      </div>
    </div>
  );

  const body = data.status === "unavailable" ? (
    <div className="p-6 text-center" role="alert" data-testid="panel-unavailable">
      <p className="font-medium text-slate-900">Fiche introuvable</p>
      <p className="mt-1 text-sm text-slate-600">Cette fiche n'existe plus ou n'est plus dans votre périmètre.</p>
      <Button variant="outline" className="mt-3" onClick={onClose}>Fermer</Button>
    </div>
  ) : data.status === "error" ? (
    <div className="p-6 text-center" role="alert" data-testid="panel-error">
      <AlertCircle className="mx-auto text-red-600" size={20} aria-hidden />
      <p className="mt-2 font-medium text-slate-900">Impossible de charger la fiche</p>
      <p className="text-sm text-slate-600">Vérifiez votre connexion puis réessayez.</p>
      <Button variant="outline" className="mt-3" onClick={data.reload} data-testid="panel-retry">Réessayer</Button>
    </div>
  ) : !ready ? (
    <div className="space-y-3 p-4" aria-busy="true" data-testid="panel-loading">
      <Skeleton className="h-16 w-full" /><Skeleton className="h-20 w-full" /><Skeleton className="h-24 w-full" />
    </div>
  ) : (
    <div className="px-4" data-testid="panel-body">
      {p.status === "lost" ? (
        <Section title="Motif de perte" testid="panel-loss"><p className="text-sm text-slate-800">{p.loss_reason || "Non renseigné"}</p></Section>
      ) : null}

      {withTasks && p.status !== "lost" && p.status !== "active_client" ? (
        <Section title="Prochaine action" testid="panel-next">
          {data.tasks === "error" ? (
            <p className="text-sm text-slate-600" role="status">Tâches indisponibles pour le moment. <button type="button" className="text-jamora hover:underline" onClick={data.reload}>Réessayer</button></p>
          ) : next ? (
            <>
              <div className="text-sm text-slate-900">{next.title}</div>
              <div className={`mt-0.5 text-xs font-semibold ${TONES[due.tone]}`} data-testid="panel-due">{due.text}</div>
              {tasks.length > 1 ? (
                <ul className="mt-2 space-y-1 text-xs text-slate-700" data-testid="panel-tasks">
                  {tasks.slice(1, 4).map((t) => <li key={t.id} className="flex justify-between gap-2"><span className="truncate">{t.title}</span><span className="shrink-0 text-slate-500">{t.due_date ? dueLabel(t, today).text : "Sans échéance"}</span></li>)}
                  {tasks.length > 4 ? <li className="text-slate-500">+ {tasks.length - 4} autre{tasks.length - 4 > 1 ? "s" : ""}</li> : null}
                </ul>
              ) : null}
            </>
          ) : (
            <p className="text-sm italic text-slate-500" data-testid="panel-no-action">Aucune action visible</p>
          )}
        </Section>
      ) : null}

      <Section title="Contact" testid="panel-contact">
        <div className="space-y-1">
          {p.phone ? <Contact icon={Phone} href={`tel:${p.phone}`} label={`Appeler ${p.pharmacy_name}`}>{p.phone}</Contact> : null}
          {p.whatsapp && p.whatsapp !== p.phone ? <Contact icon={MessageCircle}>{p.whatsapp}</Contact> : null}
          {p.email ? <Contact icon={Mail} href={`mailto:${p.email}`} label={`Écrire à ${p.pharmacy_name}`}>{p.email}</Contact> : null}
          {locationLabel(p) ? <Contact icon={MapPin}>{locationLabel(p)}</Contact> : null}
          {p.pharmacist_name || p.reference_name ? <div className="pl-6 text-xs text-slate-600">{[p.pharmacist_name && `Pharmacien : ${p.pharmacist_name}`, p.reference_name && `Référent : ${p.reference_name}`].filter(Boolean).join(" · ")}</div> : null}
          {!p.phone && !p.email && !p.whatsapp && !locationLabel(p) ? <p className="text-sm text-slate-500">Aucune coordonnée renseignée.</p> : null}
        </div>
      </Section>

      {p.status === "onboarding_in_progress" && p.onboarding ? (
        <Section title="Onboarding" testid="panel-onboarding">
          <div className="text-sm text-slate-800">Prochaine étape : <span className="font-medium">{p.onboarding.next_step || "aucune"}</span></div>
          <div className="mt-1.5 flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-slate-100"><div className="h-full rounded-full bg-jamora" style={{ width: `${p.onboarding.total ? Math.round((p.onboarding.done / p.onboarding.total) * 100) : 0}%` }} /></div>
            <span className="text-2xs font-mono text-slate-600">{p.onboarding.done}/{p.onboarding.total}</span>
          </div>
        </Section>
      ) : null}

      <Section title="Échanges" testid="panel-exchanges">
        {data.feed === "error" ? (
          <p className="text-sm text-slate-600" role="status">Échanges indisponibles pour le moment. <button type="button" className="text-jamora hover:underline" onClick={data.reload}>Réessayer</button></p>
        ) : (
          <>
            <p className="text-sm text-slate-800" data-testid="panel-last-contact">
              {exchanges.lastContact ? <>Dernier contact : <span className="font-medium">{INTERACTION_LABELS[exchanges.lastContact.type] || exchanges.lastContact.type}</span> · {formatRelativeDate(exchanges.lastContact.occurred_at)}</> : "Aucun contact enregistré"}
            </p>
            {exchanges.recent.length > 0 ? (
              <ul className="mt-2 space-y-1.5" data-testid="panel-recent">
                {exchanges.recent.map((i) => (
                  <li key={i.id} className="text-xs text-slate-700">
                    <span className="font-medium text-slate-800">{INTERACTION_LABELS[i.type] || i.type}</span> <span className="text-slate-500">· {formatRelativeDate(i.occurred_at)}</span>
                    <div className="line-clamp-2 text-slate-600">{i.content}</div>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </Section>

      {channels.length > 0 || p.source ? (
        <details className="py-3" data-testid="panel-origin">
          <summary className="cursor-pointer text-2xs font-semibold uppercase tracking-wider text-slate-500">Origine</summary>
          <div className="mt-1.5 space-y-1 text-xs text-slate-700">
            {channels.length > 0 ? <div>Canal : {channels.map((c) => ACQUISITION_CHANNEL_LABEL[c] || c).join(", ")}</div> : null}
            {p.source ? <div>Entrée dans le CRM : {prospectSourceLabel(p.source)}</div> : null}
          </div>
        </details>
      ) : null}
    </div>
  );

  const footer = (
    <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-white px-4 py-3" data-testid="panel-actions">
      {actions.map((a) => <QuickActionButton key={a.id} action={a} prospect={p} onQuick={onQuick} className={BIG} />)}
      <div className="ml-auto">
        <ProspectActionsMenu
          prospect={p} actions={actions} can={can} canAssign={canAssign} canMove={canMove} targetClass={TARGET} testPrefix="panel"
          onOpen={() => navigate(`/prospects/${id}`)} onQuick={onQuick} onMove={onMove}
        />
      </div>
    </div>
  );

  if (mode === "drawer") {
    return (
      <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetContent side="right" className="flex w-[400px] max-w-full flex-col gap-0 p-0" data-testid="quick-panel" data-mode="drawer" aria-describedby={undefined}>
          <SheetDescription className="sr-only">Fiche rapide de la pharmacie</SheetDescription>
          {header}
          <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
          {ready ? footer : null}
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <MobileSheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <MobileSheetContent closeOnHandle data-testid="quick-panel" data-mode="sheet" aria-describedby={undefined}>
        <MobileSheetHeader className="block p-0">{header}</MobileSheetHeader>
        <MobileSheetBody className="p-0">{body}</MobileSheetBody>
        {ready ? <MobileSheetFooter className="p-0">{footer}</MobileSheetFooter> : null}
      </MobileSheetContent>
    </MobileSheet>
  );
}
